/**
 * Recommended Prompts Redux Slice
 *
 * Manages state for recommended prompts data fetched from the API.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RecommendedPrompt } from '@/types/recommended-prompts.types';

interface RecommendedPromptsState {
  prompts: RecommendedPrompt[];
  isLoading: boolean;
  lastFetchTime: number | null;
  error: string | null;
}

const initialState: RecommendedPromptsState = {
  prompts: [],
  isLoading: false,
  lastFetchTime: null,
  error: null,
};

const recommendedPromptsSlice = createSlice({
  name: 'recommendedPrompts',
  initialState,
  reducers: {
    /**
     * Update the prompts array with new data
     */
    setPrompts: (state, action: PayloadAction<RecommendedPrompt[]>) => {
      state.prompts = action.payload;
      state.lastFetchTime = Date.now();
      state.error = null;
    },

    /**
     * Update the loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error message
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Reset state (used when needed to clear cache)
     */
    resetPrompts: (state) => {
      state.prompts = [];
      state.lastFetchTime = null;
      state.error = null;
      state.isLoading = false;
    },
  },
});

export const { setPrompts, setLoading, setError, resetPrompts } =
  recommendedPromptsSlice.actions;

export default recommendedPromptsSlice.reducer;
